import * as React from 'react';
import { Table, Toolbar, ToolbarItem, Button, ButtonGroup, Icon, Fieldset } from '../../common';
import { Loader } from '../../common/components/Loader';
import { PortOpeningRequestsDirection } from '../../../models/enums/POR/PortOpeningRequestsDirection';
import Paginator from '../../common/tables/Paginator';
import { RuleOperation } from '../../../models/enums/POR/RuleOperation';
import Select, { StylesConfig } from "react-select";
import './AWSRulesTable.css';
import { WorkflowState } from '../../../models/enums/WorkflowState';
import AWSPortOpeningRule from '../../../models/entities/AWSPortOpeningRule';
import CSVGeneratorButton from '../../common/CSVGeneratorButton';
import { PORColumn, RuleColumnsLayout } from './RuleColumnsLayout';
import POROperationBadge from '../../PortOpeningRequests/components/POROperationBadge';
import PORTypeBadge from '../../PortOpeningRequests/components/PORTypeBadge';
import TrashcanModalAWS from '../../AWSPortOpeningRequests/components/TrashcanModalAWS';
import { RulesFormProps } from './awsPORRule/AbstractAWSRulesForm';
import { VirtualPrivateCloud } from '../../../models/entities/AzureResources/VirtualPrivateCloud';
import RuleModalAccToInternet from './awsPORRule/RuleModalAccToInternet';
import AWSImplementedRulesModal from './AWSImplementedRulesModal';
import RuleModalAccToDMZ from './awsPORRule/RuleModalAccToDMZ';
import { AWSPortOpeningRequestsTypes } from '../../../models/enums/POR/AWSPortOpeningRequestType';
import RuleModalUntrusted from './awsPORRule/RuleModalUntrusted';
import BulkUploadModal from './awsPORRule/BulkUploadModal';

interface RulesTableProps {
	rules: AWSPortOpeningRule[];
	implementedRules?: AWSPortOpeningRule[];
	virtualPrivateClouds?: VirtualPrivateCloud[];
	secondaryVirtualPrivateClouds?: VirtualPrivateCloud[];
	porDirection: PortOpeningRequestsDirection;
	porType: AWSPortOpeningRequestsTypes;
	readonly?: boolean;
	onChange: (rules: AWSPortOpeningRule[]) => any;
	loadingImplementedRules?: boolean;
	failedImplementedRules?: boolean;
	selectedFromExisting?: string[];
	setSelectedFromExisting?: (rule: string[]) => any;
	fromDetails?: boolean;
	currentWorkflowState?: number;
}

interface RulesTableState {
	columns: PORColumn[];
	ruleModalVisible: boolean;
	editingRule?: AWSPortOpeningRule;
	bulkUploadModalVisible: boolean;
	pageRows: AWSPortOpeningRule[];
	implementedRuleModalVisible: boolean;
	optionValueSelected: string,
	showTrashcanModal: boolean;
}

export class AWSRulesTable extends React.Component<RulesTableProps, RulesTableState> {

	constructor(props: RulesTableProps | Readonly<RulesTableProps>) {
		super(props);
		this.state = {
			columns: [],
			ruleModalVisible: false,
			bulkUploadModalVisible: false,
			pageRows: [],
			implementedRuleModalVisible: false,
			showTrashcanModal: false,
			optionValueSelected: "",
		};
	}

	componentDidMount() {
		this.setColumns();
	}

	componentDidUpdate(prevProps: RulesTableProps) {
		if (prevProps.porDirection?.value !== this.props.porDirection?.value) {
			this.setColumns();
		}
	}

	setColumns() {
		this.setState({
			columns: RuleColumnsLayout.get(this.props.porDirection)
		});
	}

	getRulesModalProps(): RulesFormProps {
		let editingRule = null;
		if (this.state.editingRule != null) {
			editingRule = { ...this.state.editingRule };
			if ((editingRule.isTemporary && editingRule.operation == RuleOperation.Modify)) editingRule.operation = RuleOperation.Remove;
			editingRule.name = editingRule.awsName ?? editingRule.name;
		}

		return {
			visible: this.state.ruleModalVisible,
			onConfirm: this.handleRuleChange,
			onClose: () => this.setState({ editingRule: null, ruleModalVisible: false }),
			editingRule: editingRule,
			porDirection: this.props.porDirection,
			virtualPrivateClouds: {
				primaryAccountVirtualPrivateClouds: this.props.virtualPrivateClouds,
				secondaryAccountVirtualPrivateClouds: this.props.secondaryVirtualPrivateClouds,
			},
		};
	}

	handleRuleChange = (rule: AWSPortOpeningRule) => {
		let { rules } = this.props;
		if (this.state.editingRule) {
			const index = rules.indexOf(this.state.editingRule);
			rules = [...rules];
			rules[index] = rule;
		} else {
			rules = [...rules, rule];
		}
		this.props.onChange(rules);
		this.setState({ ruleModalVisible: false, editingRule: null });
	};

	handleDeleteAll = () => {
		this.props.onChange([]);
		this.resetTrashcanModal();
	};

	handleDeleteOrRemoveRule = (ruleToRemove: AWSPortOpeningRule) => {
		this.setState({ showTrashcanModal: true, editingRule: ruleToRemove })
	};

	handleRemoveRule = () => {
		const rules = this.props.rules.filter(rule => rule !== this.state.editingRule);
		this.props.setSelectedFromExisting(this.props.selectedFromExisting.filter(ruleName => ruleName !== this.state.editingRule.name));
		this.props.onChange(rules);
		this.resetTrashcanModal();
	};

	handleDeleteRule = () => {
		let editingRule = { ...this.state.editingRule };
		editingRule.name = editingRule.awsName ?? editingRule.name;
		editingRule.awsName = undefined;
		editingRule.operation = RuleOperation.Remove;
		this.handleRuleChange(editingRule);
		this.resetTrashcanModal();
	};


	resetTrashcanModal = () => {
		this.setState({
			editingRule: null,
			showTrashcanModal: false
		})
	}

	closeTrashcanModal = this.resetTrashcanModal;

	handleBulkUpload = (rules: AWSPortOpeningRule[]) => {
		this.setState({
			bulkUploadModalVisible: false,
			optionValueSelected: '',
		});
		this.props.onChange([...this.props.rules, ...rules]);
	};

	handlePageChange = (page: AWSPortOpeningRule[]) => {
		this.setState({
			pageRows: page
		});
	};

	handleImplementedRulesOnSuccess = (impRules: AWSPortOpeningRule[]) => {
		this.setState({
			implementedRuleModalVisible: false,
			optionValueSelected: '',
		});
		this.props.setSelectedFromExisting([...this.props.selectedFromExisting, ...impRules.map(rule => rule.name)])
		this.props.onChange([...this.props.rules, ...impRules]);
	};

	directionIs(direction: PortOpeningRequestsDirection) {
		return this.props.porDirection === direction;
	}

	getOperationName(rule: AWSPortOpeningRule) {
		if (this.props.fromDetails && this.props.currentWorkflowState !== WorkflowState.Review.value && this.props.currentWorkflowState !== WorkflowState.Draft.value) {
			return this.state.columns.find(column => column.header === 'Operation').cell(rule)
		}
		const operationColumn = this.state.columns.find(column => column.header === 'Operation')
		return operationColumn.cell(rule)

	}

	handleOptionSelected = (option) => {
		this.setState({ optionValueSelected: option.value });
		if (option.value === "import-new-rules") {
			if (!option.isDisabled) this.setState({ bulkUploadModalVisible: true });
		} else {
			if (!option.isDisabled) this.setState({ implementedRuleModalVisible: true });
		}
	}

	CustomOption = (props: { data: any; innerRef: any; innerProps: any; isDisabled: boolean }) => {
		const { data, innerRef, innerProps, isDisabled } = props;
		return (
			<div
				style={{ cursor: `${isDisabled ? 'not-allowed' : 'pointer'}` }}
				ref={innerRef}
				{...innerProps}
				className='custom-option-box'
			>
				<div style={{ color: `${isDisabled ? '#999' : '#1E55B1'}`, fontSize: 'large', }}>{data.isLoading ? data.loadingMessage : data.label}</div>
				<div style={{ color: '#999', fontSize: 'small', wordWrap: 'break-word', width: '180px' }}>{data.subtext}</div>
			</div>
		)
	}

	styles: StylesConfig<any, false> = {
		control: (css) => ({ ...css, width: 200, zIndex: 5 }),
	};

	isTypeAndDirectionDefine = () => {
		return this.props.porType != undefined && this.props.porDirection != undefined
	}

	isExternalAccountsScope() {
		return this.props.porDirection != undefined && this.props.porDirection.name == PortOpeningRequestsDirection.ExternalAccounts.name
	}

	modalRender = () => {
		const modalOptions = {
			AccountToInternet: <RuleModalAccToInternet
									{...this.getRulesModalProps()}
									isReadOnly={this.props.readonly}
								/>,
			AccountToDMZ: <RuleModalAccToDMZ
								{...this.getRulesModalProps()}
								isReadOnly={this.props.readonly}
						  />,
			Untrusted: <RuleModalUntrusted
								{...this.getRulesModalProps()}
								isReadOnly={this.props.readonly}
						/>					
		}

		return this.isTypeAndDirectionDefine() ? modalOptions[this.props.porType.name] : "";
	}

	render() {
		const tableHeaders = this.state.columns?.filter(column => column.header !== "Protocol" && column.header !== "Description" && column.header !== "Region").map((column) => (
			<Table.Head.Cell key={column.header} className="rules-table-head-cell">
				{column.header}
			</Table.Head.Cell>
		));
		const tableRows = [];
		const defaultLimit = 10;
		const virtualPrivateClouds = this.props.virtualPrivateClouds ?? [];
		const options = [
			{
				value: "import-new-rules",
				label: 'Import new rules',
				subtext: 'Add new rules at the same time with CSV file',
				isDisabled: this.props.porDirection == null || !(virtualPrivateClouds.length > 0),
				isLoading: this.props.porDirection == null || !(virtualPrivateClouds.length > 0),
				loadingMessage: this.props.porDirection != null ? 'Loading Virtual Networks...' : 'Please select a POR Direction'
			},
			{
				value: "import-existing-rule",
				label: 'Import existing rules',
				subtext: 'Add existing rules to delete or modify from this account',
				isDisabled: this.props.loadingImplementedRules || !(this.props.implementedRules?.length > 0),
				isLoading: this.props.loadingImplementedRules || !(this.props.implementedRules?.length > 0),
				loadingMessage: (!(this.props.loadingImplementedRules) && this.props.implementedRules?.length == 0) ? 'No Rules Implemented' : 'Loading Existing Rules...'
			},
		];

		this.state.pageRows.forEach((rule, rowIndex) => {
			tableRows.push(
				<Table.Row key={rowIndex}>
					{this.state.columns?.filter(column => column.header !== "Protocol" && column.header !== "Description" && column.header !== "Region").map((column, colIndex) => (
						<Table.Cell
							key={`${rowIndex}-${colIndex}`}
							className="em-u-margin-right-auto"
							title={(column.cell(rule) || '-')}
						>
							{column.header === 'Operation' ? (<POROperationBadge>{this.getOperationName(rule)}</POROperationBadge>)
								: (column.header === 'Type' ? (<PORTypeBadge >{column.cell(rule)}</PORTypeBadge>)
									: (column.cell(rule)?.length > (column.limit || defaultLimit) ?
										(`${column.cell(rule).trim().substring(0, (column.limit || defaultLimit))}...`)
										:
										(column.cell(rule) || '-')))}
						</Table.Cell>
					))}
					<Table.Cell>
						{!this.props.readonly ?
							<ButtonGroup optionalClass="rules-table-btn-group">
								<Button
									style={{ zIndex: 0 }}
									variant="primary"
									size="small"
									onClick={() => this.setState({ ruleModalVisible: true, editingRule: rule })}
								>
									<Icon size="small" icon="pencil" />
								</Button>
								<Button
									style={{ zIndex: 0 }}
									variant="primary"
									size="small"
									onClick={() => this.handleDeleteOrRemoveRule(rule)}
								>
									<Icon size="small" icon="trashcan" />
								</Button>
							</ButtonGroup>
							:
							<Button
								style={{ zIndex: 0 }}
								variant="primary"
								size="small"
								onClick={() => this.setState({ ruleModalVisible: true, editingRule: rule })}
							>
								See details
							</Button>
						}
					</Table.Cell>
				</Table.Row>
			);
		});

		return (
			<>
				<Fieldset legend="Ruleset">
					<Table
						header={
							<Toolbar>
								{this.props.readonly &&
									<ToolbarItem right>
										<CSVGeneratorButton
											data={this.props.rules}
											columns={this.state.columns}
										/>
									</ToolbarItem>}
								{!this.props.readonly &&
									<ToolbarItem right>
										<ButtonGroup>
											<Button
												variant="primary"
												size="small"
												disabled={!(this.props.virtualPrivateClouds?.length > 0) && !this.isExternalAccountsScope()}
												onClick={() => this.setState({ ruleModalVisible: true })}
											>
												{!(virtualPrivateClouds.length > 0) && !this.isExternalAccountsScope() ?
													<>
														{(this.props.failedImplementedRules) ?
															<>
																<Loader className="icon-loader em-c-icon--small" />
																<span>Operation Failed</span>
															</>
															:
															<>
																<Loader className="icon-loader em-c-icon--small" />
																<span>Loading Virtual Networks...</span>
															</>}
													</>
													:
													<>
														<Icon className="em-u-margin-right-half" size="small" icon="plus-filled" />
														<span>Add New Rule</span>
													</>}
											</Button>
											<Select
												placeholder='Import rules'
												value={this.state.optionValueSelected != null ? this.state.optionValueSelected : ''}
												onChange={(option) => this.handleOptionSelected(option)}
												components={{ Option: this.CustomOption }}
												options={options}
												styles={this.styles}
											/>
										</ButtonGroup>
									</ToolbarItem>}
							</Toolbar>
						}
						footer={
							<Paginator
								data={this.props.rules}
								onPageChange={this.handlePageChange}
							/>
						}
					>
						<Table.Head>
							<Table.Head.Row>
								{tableHeaders}
								<Table.Head.Cell>
									{!this.props.readonly ? 'Actions' : ''}
								</Table.Head.Cell>
							</Table.Head.Row>
						</Table.Head>
						<Table.Body>
							{tableRows.length > 0 ?
								tableRows
								:
								<Table.Row>
									<Table.Cell
										colSpan={this.state.columns?.length + 2 || 2}
									>
										<i>No rules found.</i>
									</Table.Cell>
								</Table.Row>}
						</Table.Body>
					</Table>
				</Fieldset>
				{this.state.showTrashcanModal &&
					<TrashcanModalAWS
						onClose={this.resetTrashcanModal}
						onDelete={this.handleDeleteRule}
						onRemove={this.handleRemoveRule}
					/>
				}
				{this.modalRender()}
				{this.state.implementedRuleModalVisible &&
					<AWSImplementedRulesModal
						direction={this.props.porDirection}
						rules={this.props.implementedRules}
						onSuccess={this.handleImplementedRulesOnSuccess}
						visible={this.state.implementedRuleModalVisible}
						alreadySelected={this.props.selectedFromExisting}
						onCancel={(ev) => {
							this.setState({
								implementedRuleModalVisible: false,
								optionValueSelected: '',
							});
						}}
					/>}
				{this.state.bulkUploadModalVisible &&
				<BulkUploadModal
					visible={this.state.bulkUploadModalVisible}
					direction={this.props.porDirection}
					onSuccess={this.handleBulkUpload}
					virtualPrivateClouds={this.props.virtualPrivateClouds}
					onCancel={() => {
						this.setState({
							bulkUploadModalVisible: false,
							optionValueSelected: '',
						});
					}}
				/>}
			</>
		);
	}
}